import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Link2, Check, Loader, Edit2 } from "lucide-react";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

export function WebsiteCard() {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentWebsite, setCurrentWebsite] = useState("");
  const [editMode, setEditMode] = useState(false);

  const API_URL = `${BASE_URL}/auth/userinfo-website/`;

  const getToken = () =>
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const isValidFullUrl = (url) => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return false;
      }
      if (!parsed.hostname || !parsed.hostname.includes(".")) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const handleSaveWebsite = async () => {
    if (!websiteUrl) {
      toast.error("Please enter a website URL");
      return;
    }

    if (!isValidFullUrl(websiteUrl)) {
      toast.error("Invalid URL. Example: https://example.com");
      return;
    }

    setIsLoading(true);

    try {
      const token = getToken();
      if (!token) return toast.error("Please log in");

      const headers = buildHeaders(token);

      const response = await fetch(API_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ website: websiteUrl }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || "Failed to save website");
      }

      toast.success(editMode ? "Website updated!" : "Website connected!");
      setCurrentWebsite(websiteUrl);
      setWebsiteUrl("");
      setEditMode(false);
    } catch (error) {
      toast.error(error.message || "Error while saving website");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentWebsite = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const headers = buildHeaders(token);

      const response = await fetch(API_URL, {
        method: "GET",
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentWebsite(data.website);
      }
    } catch (error) {
      console.error("Error fetching website:", error);
    }
  };

  useEffect(() => {
    fetchCurrentWebsite();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-5 flex flex-col gap-4 border border-gray-100"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
          <Globe className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-normal text-gray-800">
            Connect Your Website
          </h2>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Website URL</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <Link2 className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="https://example.com"
            value={websiteUrl}
            disabled={isLoading}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg 
                     focus:border-blue-500 focus:ring-2 focus:ring-blue-100 
                     outline-none transition text-gray-700
                     disabled:bg-gray-50"
          />
        </div>
      </div>

      <AnimatePresence>
        {currentWebsite && !editMode && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Connected
                    </p>
                    <p className="text-sm text-green-600 truncate max-w-[200px]">
                      {currentWebsite}
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditMode(true);
                    setWebsiteUrl(currentWebsite);
                  }}
                  className="px-3 py-1.5 text-sm bg-white border border-green-300 
                           text-green-700 rounded-lg font-medium hover:bg-green-50 
                           transition flex items-center gap-2"
                >
                  <Edit2 className="w-3 h-3" />
                  Edit
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={handleSaveWebsite}
        disabled={isLoading || !websiteUrl}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`
  w-full py-3 rounded-lg font-medium text-white
  transition-all flex items-center justify-center gap-2
  ${
    isLoading
      ? "bg-gray-400 cursor-not-allowed"
      : editMode
        ? "bg-blue-600 hover:bg-blue-700"
        : "bg-green-600 hover:bg-green-700"
  }
  disabled:opacity-50 disabled:cursor-not-allowed
`}
      >
        {isLoading ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : editMode ? (
          "Update Website"
        ) : currentWebsite ? (
          "Click Edit to Change Website"
        ) : (
          "Connect Website"
        )}
      </motion.button>
    </motion.div>
  );
}
